/**
 * 사례개념화 정리 (Amendment A38): 가설·의견이 너무 많을 때 외부 AI에 보내 겹치는 것을 합치고,
 * 연결 못한 AI 의견을 알맞은 가설에 넣어 칸마다 핵심 몇 개로 줄인다.
 * - 앱이 항목마다 번호(H 가설·L 목록·O 의견)를 붙여 보내고, AI는 "어느 번호들을 합쳐 무슨 문장으로"만 답한다.
 * - 상담자가 미리보기에서 고른 것만 개정안(제안)에 넣는다. 승인은 따로 (AI가 승인·확인됨을 만들 수 없음).
 * - 합친 가설은 첫 가설의 id를 이어 받는다 (같은 가설은 같은 id, A5). 버린 가설은 폐기로 기록.
 * - AI가 말하지 않은 번호는 그대로 둔다 (유실 없음).
 */
import type { AnalysisItem, CaseFormulationRevision } from '../schema/v1';
import {
  FORMULATION_ITEM_FIELDS,
  FORMULATION_LABELS,
  FORMULATION_LIST_FIELDS,
  PENDING_KIND_LABEL,
  parsePendingOpinions,
  removePendingLine,
  type PendingOpinion,
} from '../workflow/formulation';
import { parseJsonLoose, str, strArr, type ParseFail, type ParseOk } from './parse';
import { BEGIN, END } from './prompts';
import { TRANSCRIPT_LEGEND } from './packet';

export const TIDY_PACKET_VERSION = 'FORMULATION_TIDY_PACKET_V1';
export const TIDY_FORMAT = 'FORMULATION_TIDY_V1';
export const TIDY_TASK_VERSION = 'formulation-tidy-task-1.1';

export type ItemField = (typeof FORMULATION_ITEM_FIELDS)[number];
export type ListField = (typeof FORMULATION_LIST_FIELDS)[number];
export type TidyField = ItemField | ListField;
const ITEM_FIELDS = new Set<string>(FORMULATION_ITEM_FIELDS);
const LIST_FIELDS = new Set<string>(FORMULATION_LIST_FIELDS);
const isItemField = (f: string): f is ItemField => ITEM_FIELDS.has(f);
const isListField = (f: string): f is ListField => LIST_FIELDS.has(f);

/** 정리 대상 내용 (개정안의 칸들과 변경 이유) */
export type TidyTarget = Partial<
  Pick<
    CaseFormulationRevision,
    | ItemField
    | ListField
    | 'addedHypotheses'
    | 'strengthenedHypotheses'
    | 'weakenedHypotheses'
    | 'rejectedHypotheses'
    | 'changeReason'
  >
>;

export type TidySource =
  | { key: string; kind: 'item'; field: ItemField; item: AnalysisItem }
  | { key: string; kind: 'list'; field: ListField; text: string }
  | { key: string; kind: 'opinion'; opinion: PendingOpinion };

export const sourceText = (s: TidySource) =>
  s.kind === 'item' ? s.item.text : s.kind === 'list' ? s.text : s.opinion.text;

/** 항목마다 번호: 가설 H1…, 목록 L1…, 연결 못한 의견 O1… (같은 내용이면 같은 번호) */
export function tidySources(t: TidyTarget): TidySource[] {
  const out: TidySource[] = [];
  let h = 0;
  let l = 0;
  for (const field of FORMULATION_ITEM_FIELDS)
    for (const item of t[field] ?? []) if (item.text.trim()) out.push({ key: `H${++h}`, kind: 'item', field, item });
  for (const field of FORMULATION_LIST_FIELDS)
    for (const text of t[field] ?? []) if (text.trim()) out.push({ key: `L${++l}`, kind: 'list', field, text });
  parsePendingOpinions(t.changeReason).forEach((opinion, i) =>
    out.push({ key: `O${i + 1}`, kind: 'opinion', opinion }),
  );
  return out;
}

/** 보낸 때와 받은 때의 개정안이 같은지 확인하는 짧은 표시 */
export function tidyBasis(sources: TidySource[]): string {
  let x = 5381;
  for (const ch of sources.map((s) => `${s.key}|${sourceText(s)}`).join('\n'))
    x = ((x << 5) + x + ch.charCodeAt(0)) | 0;
  return (x >>> 0).toString(36);
}

export function tidyCounts(sources: TidySource[]) {
  return {
    items: sources.filter((s) => s.kind === 'item').length,
    lists: sources.filter((s) => s.kind === 'list').length,
    opinions: sources.filter((s) => s.kind === 'opinion').length,
  };
}

const STATUS_KO: Record<AnalysisItem['status'], string> = {
  observation: '관찰',
  hypothesis: '가설',
  confirmed: '확인됨',
};

export const TIDY_TASK = `위 ${TIDY_PACKET_VERSION}의 [지금 개정안]은 항목이 너무 많고 서로 겹친다. 상담자가 한눈에 보고 쓸 수 있게 정리하라.
- 같은 뜻이거나 겹치는 항목은 하나로 합친다. 칸마다 핵심 3~5개를 넘지 않게 한다 (근거가 분명히 다른 것은 따로 둔다).
- [연결 못한 AI 의견](O)은 알맞은 항목에 합치거나(from에 넣는다), 새 항목으로 두거나, 근거가 약하면 버린다.
- 내담자의 강점·자원(지지체계, 신앙공동체, 대처능력 등)은 protectiveFactors 칸에 둔다.
- 신념 가설 칸에 몰려 있는 항목 중 다른 칸이 맞는 것(정서는 coreEmotions, 생각의 주제는 coreCognitiveThemes, 반복 행동은 behavioralPatterns, 관계 방식은 relationalPatterns, 촉발·취약·유지 요인 등)은 그 칸으로 옮긴다 (field를 그 칸으로).
- 비어 있는 칸((없음))도 [회기 요약]·기존 항목에 근거가 있으면 채운다 — 새 항목은 from을 빈 목록 []으로. 근거 없이 칸을 채우려고 지어내지 않는다.
- 합친 문장은 한두 문장으로 짧게. 확인되지 않은 것은 가설 말투로("~일 수 있다"). 회기 요약에 없는 새 사실을 지어내지 않는다. 상담자의 질문·해석·재진술은 내담자 사실이 아니다.
- 모든 H·L·O 번호는 items의 from 또는 dropped 중 한 곳에 꼭 한 번만 나온다. 바꿀 필요 없는 항목은 from에 자기 번호 하나와 같은 문장으로 그대로 둔다.
- field는 [지금 개정안]의 ( ) 안 영어 이름 중 하나를 쓴다. 이름·기관명 등 식별정보는 쓰지 않는다.
- why에는 무엇을 왜 합쳤는지 한 줄로.

출력은 아래 JSON 하나만, ${BEGIN} 와 ${END} 사이에:
${BEGIN}
{"formatVersion":"${TIDY_FORMAT}","basis":"(메타의 기준 값)","items":[{"field":"beliefHypotheses","text":"","from":["H1","H4","O2"],"why":""}],"dropped":[{"from":"H7","why":""}],"notes":""}
${END}`;

/** 전사까지 보내 처음부터 다시 세우기: 지난 분석이 만든 항목은 전사로 확인되는 것만 살린다 */
export const REBUILD_TASK = `위 ${TIDY_PACKET_VERSION}의 [회기 전사]를 근거로 이 내담자의 사례개념화를 처음부터 다시 세워라.
- [지금 개정안]과 [연결 못한 AI 의견]의 번호 항목은 이전 분석이 만든 것이라 질이 고르지 않다. 전사로 확인되는 것만 살린다 — 살릴 때는 from에 그 번호를 넣고 문장은 전사에 맞게 새로 다듬는다. 확인되지 않거나 겹치거나 지나친 추론이면 dropped에 넣는다.
- 모든 칸을 채운다 (근거가 있는 것만): currentProblems, precipitatingFactors, predisposingFactors, perpetuatingFactors, protectiveFactors, coreEmotions, coreCognitiveThemes, beliefHypotheses, behavioralPatterns, relationalPatterns, allianceFactors, therapyInterferingPatterns. 전사에서 새로 찾은 항목은 from을 빈 목록 []으로.
- 칸마다 핵심 3~5개. 촉발·취약·유지·보호요인을 구분하고, 정서·인지·행동·관계를 각 칸에 나눈다. 신념 가설에는 신념에 관한 가설만.
- 내담자가 직접 말한 것(내)을 근거로 한다. 상담자의 질문·해석·재진술(상)은 내담자 사실이 아니다. 화자가 추정(내?·상?)이거나 판정 불가(?)인 줄만으로는 단정하지 않는다.
- 확인되지 않은 것은 가설 말투로("~일 수 있다"). 전사에 없는 사실을 지어내지 않는다. 이름·기관명 등 식별정보는 쓰지 않는다.
- why에 근거 회기와 발언 번호를 적는다 (예: S2 g14, S3 a40).
- 모든 H·L·O 번호는 items의 from 또는 dropped 중 한 곳에 꼭 한 번만 나온다.

출력은 아래 JSON 하나만, ${BEGIN} 와 ${END} 사이에:
${BEGIN}
{"formatVersion":"${TIDY_FORMAT}","basis":"(메타의 기준 값)","items":[{"field":"perpetuatingFactors","text":"","from":[],"why":"S2 g14"}],"dropped":[{"from":"H7","why":""}],"notes":""}
${END}`;

export type TidyPacketInput = {
  /** 전사까지 보내 처음부터 다시 세우기 (회기별 전사 줄) */
  transcripts?: { sessionNumber: number; sessionDate: string; lines: string[] }[];
  clientCode: string;
  version: number;
  presentingProblems?: string[];
  sessions: {
    sessionNumber: number;
    sessionDate: string;
    summary?: string;
    /** 그 회기 분석에 적힌 주요 사건·변화·정서·인지·행동·관계 (칸을 채울 근거) */
    notes?: { label: string; items: string[] }[];
  }[];
  target: TidyTarget;
  request?: string;
};

export function buildTidyPacket(i: TidyPacketInput): string {
  const sources = tidySources(i.target);
  const out = [`=== ${TIDY_PACKET_VERSION} ===`];
  out.push(
    `[메타] 출력 형식: ${TIDY_FORMAT} · 내담자: ${i.clientCode} · 개정안 v${i.version} · 기준: ${tidyBasis(sources)}`,
  );
  if (i.presentingProblems?.length) out.push(`주호소:\n${i.presentingProblems.map((p) => `- ${p}`).join('\n')}`);
  const sums = i.sessions.filter((s) => s.summary?.trim() || s.notes?.some((n) => n.items.length));
  if (sums.length)
    out.push(
      `[회기 요약] (회기마다 요약과 그 회기 분석에 적힌 항목 — 칸을 채울 근거)\n${sums
        .map((s) =>
          [
            `S${s.sessionNumber} (${s.sessionDate}): ${s.summary?.trim() ?? ''}`,
            ...(s.notes ?? []).filter((n) => n.items.length).map((n) => `  - ${n.label}: ${n.items.join(' / ')}`),
          ].join('\n'),
        )
        .join('\n')}`,
    );
  const rebuild = !!i.transcripts?.length;
  if (rebuild) {
    out.push('[회기 전사]', TRANSCRIPT_LEGEND);
    for (const t of i.transcripts!) out.push('', `[S${t.sessionNumber} · ${t.sessionDate}]`, ...t.lines);
    out.push('');
  }
  out.push(rebuild ? '[지금 개정안] (이전 분석이 만든 항목 — 전사로 확인되는 것만 살린다)' : '[지금 개정안]');
  const fields: TidyField[] = [...FORMULATION_ITEM_FIELDS, ...FORMULATION_LIST_FIELDS];
  for (const f of fields) {
    const rows = sources.filter((s) => s.kind !== 'opinion' && s.field === f);
    out.push(`## ${FORMULATION_LABELS[f]} (${f})`);
    out.push(
      rows.length
        ? rows
            .map((s) =>
              s.kind === 'item' ? `${s.key} [${STATUS_KO[s.item.status]}] ${s.item.text}` : `${s.key} ${sourceText(s)}`,
            )
            .join('\n')
        : '(없음)',
    );
  }
  const ops = sources.filter((s) => s.kind === 'opinion');
  if (ops.length) {
    out.push('[연결 못한 AI 의견] (지난 AI 분석이 기존 가설과 연결하지 못한 의견)');
    for (const s of ops)
      if (s.kind === 'opinion') out.push(`${s.key} (${PENDING_KIND_LABEL[s.opinion.kind]}) ${s.opinion.text}`);
  }
  if (i.request?.trim()) out.push(`[상담자 요청]\n${i.request.trim()}`);
  out.push(`=== END ${TIDY_PACKET_VERSION} ===`, '', rebuild ? REBUILD_TASK : TIDY_TASK);
  return out.join('\n');
}

// ---------------- 답 읽기 ----------------

export type TidyAnswerItem = { field: TidyField; text: string; from: string[]; why?: string };
export type TidyAnswer = {
  basis?: string;
  items: TidyAnswerItem[];
  dropped: { from: string; why?: string }[];
  notes?: string;
};

const asObj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const asObjArr = (v: unknown) => (Array.isArray(v) ? v.map(asObj) : []);
const keyOf = (v: string) => v.trim().toUpperCase();

export function parseTidy(raw: string): ParseOk<TidyAnswer> | ParseFail {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = asObj(j.value);
  const warnings: string[] = [];
  const items: TidyAnswerItem[] = [];
  for (const x of asObjArr(o.items)) {
    const field = str(x.field);
    const text = str(x.text);
    if (!text) continue;
    if (!field || !(isItemField(field) || isListField(field))) {
      warnings.push(`알 수 없는 칸 "${field ?? ''}" — 건너뜀`);
      continue;
    }
    const why = str(x.why);
    items.push({ field, text, from: strArr(x.from).map(keyOf), ...(why ? { why } : {}) });
  }
  const dropped = asObjArr(o.dropped).flatMap((x) => {
    const from = str(x.from);
    const why = str(x.why);
    return from ? [{ from: keyOf(from), ...(why ? { why } : {}) }] : [];
  });
  if (!items.length && !dropped.length) return { ok: false, errors: ['정리한 항목(items)이 없습니다'] };
  if (o.formatVersion !== TIDY_FORMAT) warnings.push(`formatVersion 없음 (${TIDY_FORMAT}로 간주)`);
  if (j.truncated) warnings.push('응답이 중간에 끊겼습니다 — 받은 것만 보여줍니다');
  const basis = str(o.basis);
  const notes = str(o.notes);
  return { ok: true, value: { items, dropped, ...(basis ? { basis } : {}), ...(notes ? { notes } : {}) }, warnings };
}

// ---------------- 미리보기 ----------------

export type TidyRow = {
  index: number;
  field: TidyField;
  text: string;
  why?: string;
  sources: TidySource[];
  /** keep: 한 항목을 그대로 / edit: 한 항목 문장만 / merge: 여러 개를 합침 / new: 번호 없이 새로 */
  change: 'keep' | 'edit' | 'merge' | 'new';
  /** 상담자가 확인해 둔 항목이 들어 있으면 합치거나 고치면 가설로 바뀐다 */
  hasConfirmed: boolean;
};
export type TidyDrop = { index: number; source: TidySource; why?: string };
export type TidyPlan = {
  rows: TidyRow[];
  drops: TidyDrop[];
  /** AI가 말하지 않은 항목 (그대로 둔다) */
  untouched: TidySource[];
  unknownKeys: string[];
  /** 보낸 뒤 개정안이 바뀜 */
  stale: boolean;
  before: ReturnType<typeof tidyCounts>;
  after: { items: number; lists: number; opinions: number };
  notes?: string;
};

export function planTidy(target: TidyTarget, answer: TidyAnswer): TidyPlan {
  const sources = tidySources(target);
  const byKey = new Map(sources.map((s) => [s.key, s]));
  const used = new Set<string>();
  const unknown = new Set<string>();
  const take = (k: string) => {
    const s = byKey.get(k);
    if (!s) {
      unknown.add(k);
      return undefined;
    }
    if (used.has(k)) return undefined;
    used.add(k);
    return s;
  };
  const rows: TidyRow[] = answer.items.map((x, index) => {
    const srcs = x.from.map(take).filter((s): s is TidySource => !!s);
    const content = srcs.filter((s) => s.kind !== 'opinion');
    const one = content.length === 1 ? content[0]! : undefined;
    const change: TidyRow['change'] = !srcs.length
      ? 'new'
      : content.length > 1 || (content.length === 1 && srcs.length > 1)
        ? 'merge'
        : one && one.field === x.field && sourceText(one).trim() === x.text.trim()
          ? 'keep'
          : one
            ? 'edit'
            : 'new';
    return {
      index,
      field: x.field,
      text: x.text,
      ...(x.why ? { why: x.why } : {}),
      sources: srcs,
      change,
      hasConfirmed: change !== 'keep' && srcs.some((s) => s.kind === 'item' && s.item.status === 'confirmed'),
    };
  });
  const drops: TidyDrop[] = answer.dropped.flatMap((d, index) => {
    const s = take(d.from);
    return s ? [{ index, source: s, ...(d.why ? { why: d.why } : {}) }] : [];
  });
  const untouched = sources.filter((s) => !used.has(s.key));
  const after = {
    items: rows.filter((r) => isItemField(r.field)).length + untouched.filter((s) => s.kind === 'item').length,
    lists: rows.filter((r) => isListField(r.field)).length + untouched.filter((s) => s.kind === 'list').length,
    opinions: untouched.filter((s) => s.kind === 'opinion').length,
  };
  return {
    rows,
    drops,
    untouched,
    unknownKeys: [...unknown],
    stale: !!answer.basis && answer.basis !== tidyBasis(sources),
    before: tidyCounts(sources),
    after,
    ...(answer.notes ? { notes: answer.notes } : {}),
  };
}

/** 미리보기에서 기본으로 고를 것: 바뀌는 줄은 모두, 버리기는 확인된 항목만 빼고 */
export function defaultTidyChoice(plan: TidyPlan) {
  return {
    rows: plan.rows.filter((r) => r.change !== 'keep').map((r) => r.index),
    drops: plan.drops
      .filter((d) => !(d.source.kind === 'item' && d.source.item.status === 'confirmed'))
      .map((d) => d.index),
  };
}

// ---------------- 넣기 ----------------

/**
 * 고른 줄·버리기만 개정안 내용에 넣는다. 고르지 않은 줄의 원래 항목은 그대로 남는다.
 * 돌려주는 patch는 개정안 update에 그대로 쓴다.
 */
export function applyTidy(
  target: TidyTarget,
  plan: TidyPlan,
  /** dropUntouched: AI가 말하지 않은 항목도 뺀다 (처음부터 다시 세우기) */
  choice: { rows: number[]; drops: number[]; dropUntouched?: boolean },
  newId: () => string,
): { patch: TidyTarget; merged: number; dropped: number } {
  const items = Object.fromEntries(FORMULATION_ITEM_FIELDS.map((f) => [f, [...(target[f] ?? [])]])) as Record<
    ItemField,
    AnalysisItem[]
  >;
  const lists = Object.fromEntries(FORMULATION_LIST_FIELDS.map((f) => [f, [...(target[f] ?? [])]])) as Record<
    ListField,
    string[]
  >;
  const ids = {
    added: new Set(target.addedHypotheses ?? []),
    strengthened: new Set(target.strengthenedHypotheses ?? []),
    weakened: new Set(target.weakenedHypotheses ?? []),
    rejected: new Set(target.rejectedHypotheses ?? []),
  };
  let reason = target.changeReason;
  const remap = new Map<string, string>();
  const gone = new Set<string>();

  /** 원래 자리에서 뺀다. 같은 칸이면 그 자리 번호를 돌려준다 (새 항목을 그 자리에) */
  const removeFrom = (s: TidySource, field: TidyField): number | undefined => {
    if (s.kind === 'item') {
      const arr = items[s.field];
      const at = arr.findIndex((x) => x.id === s.item.id);
      if (at >= 0) arr.splice(at, 1);
      return s.field === field && at >= 0 ? at : undefined;
    }
    if (s.kind === 'list') {
      const arr = lists[s.field];
      const at = arr.indexOf(s.text);
      if (at >= 0) arr.splice(at, 1);
      return s.field === field && at >= 0 ? at : undefined;
    }
    reason = removePendingLine(reason, s.opinion.line);
    return undefined;
  };

  const rows = plan.rows.filter((r) => choice.rows.includes(r.index) && r.change !== 'keep');
  for (const r of rows) {
    const firstItem = r.sources.find((s): s is Extract<TidySource, { kind: 'item' }> => s.kind === 'item');
    let at: number | undefined;
    for (const s of r.sources) {
      const p = removeFrom(s, r.field);
      if (at === undefined && p !== undefined) at = p;
    }
    if (isItemField(r.field)) {
      const id = firstItem?.item.id ?? newId();
      const itemSrcs = r.sources.filter((s): s is Extract<TidySource, { kind: 'item' }> => s.kind === 'item');
      for (const s of itemSrcs) if (s.item.id !== id) remap.set(s.item.id, id);
      const evidence = [...new Set(itemSrcs.flatMap((s) => s.item.evidenceIds ?? []))];
      const allObs = itemSrcs.length > 0 && itemSrcs.every((s) => s.item.status === 'observation');
      const item: AnalysisItem = {
        id,
        text: r.text,
        status: allObs ? 'observation' : 'hypothesis',
        ...(evidence.length ? { evidenceIds: evidence } : {}),
      };
      const arr = items[r.field];
      arr.splice(at ?? arr.length, 0, item);
      if (!firstItem) ids.added.add(id);
      for (const s of r.sources)
        if (s.kind === 'opinion') (s.opinion.kind === 'strengthened' ? ids.strengthened : ids.weakened).add(id);
    } else {
      for (const s of r.sources) if (s.kind === 'item') gone.add(s.item.id);
      const arr = lists[r.field];
      if (!arr.includes(r.text)) arr.splice(at ?? arr.length, 0, r.text);
    }
  }
  const drops = plan.drops.filter((d) => choice.drops.includes(d.index));
  for (const d of drops) {
    removeFrom(d.source, d.source.kind === 'opinion' ? 'currentProblems' : d.source.field);
    if (d.source.kind === 'item') ids.rejected.add(d.source.item.id);
  }
  const leftovers = choice.dropUntouched ? plan.untouched : [];
  for (const s of leftovers) {
    removeFrom(s, s.kind === 'opinion' ? 'currentProblems' : s.field);
    if (s.kind === 'item') ids.rejected.add(s.item.id);
  }
  const droppedCount = drops.length + leftovers.length;
  const map = (set: Set<string>) => [
    ...new Set([...set].filter((id) => !gone.has(id)).map((id) => remap.get(id) ?? id)),
  ];
  const live = new Set(FORMULATION_ITEM_FIELDS.flatMap((f) => items[f].map((x) => x.id)));
  const rejected = [...ids.rejected].filter((id) => !remap.has(id) && !gone.has(id));
  const merged = rows.filter((r) => r.change === 'merge').length;
  const summary = `AI 정리: 항목 ${plan.before.items + plan.before.lists}개 → ${
    FORMULATION_ITEM_FIELDS.reduce((n, f) => n + items[f].length, 0) +
    FORMULATION_LIST_FIELDS.reduce((n, f) => n + lists[f].length, 0)
  }개 (합침 ${merged}, 버림 ${droppedCount})`;
  const lines = (reason ?? '').split('\n').filter((l) => l.trim() && !l.startsWith('AI 정리:'));
  const patch: TidyTarget = {
    ...items,
    ...lists,
    addedHypotheses: map(ids.added).filter((id) => live.has(id)),
    strengthenedHypotheses: map(ids.strengthened).filter((id) => live.has(id)),
    weakenedHypotheses: map(ids.weakened).filter((id) => live.has(id)),
    rejectedHypotheses: rejected,
    changeReason: [...lines, summary].join('\n'),
  };
  return { patch, merged, dropped: droppedCount };
}
